/**
 * Reactions in a live, as the pages draw them (`prototype/live-sessions.html`,
 * screens 1 to 6): what one second of the room's totals floats up the
 * picture, the viewer's own taps left out of those totals, and the studio's
 * quiet line about the room. The five, the limit and the wire shapes are in
 * `shared/live-reactions.ts`.
 */
import {
  REACTION_KINDS,
  REACTIONS,
  type Reaction,
  type ReactionCounts,
  type ReactionKind,
} from "../../../shared/live-reactions";

export const REACTION_OF = Object.fromEntries(REACTIONS.map((r) => [r.kind, r])) as Record<ReactionKind, Reaction>;

/** The kinds sent, the most first; a tie keeps the list's own order. */
export function byCount(counts: ReactionCounts): Array<[ReactionKind, number]> {
  return REACTION_KINDS.map((k): [ReactionKind, number] => [k, counts[k] ?? 0])
    .filter(([, n]) => n > 0)
    .toSorted((a, b) => b[1] - a[1]);
}

export function countOf(counts: ReactionCounts): number {
  return REACTION_KINDS.reduce((sum, k) => sum + (counts[k] ?? 0), 0);
}

export function addCounts(a: ReactionCounts, b: ReactionCounts): ReactionCounts {
  const out: ReactionCounts = { ...a };
  for (const k of REACTION_KINDS) {
    if (b[k]) out[k] = (out[k] ?? 0) + (b[k] ?? 0);
  }
  return out;
}

// ── one second of the room, drawn ───────────────────────────────────────
/** Three or more of one kind in a second rise as one bigger reaction with its number. */
const BURST_FROM = 3;

/** How a float looks: a single, a burst with its number, a burst's small companion, or the live's echo on the replay. */
export type FloatLook = "one" | "burst" | "kin" | "echo";

export interface Float {
  kind: ReactionKind;
  look: FloatLook;
  /** The number a burst carries. */
  count?: number;
  delayMs: number;
  /** Across the lane, in % of its width from the middle. */
  shift: number;
  /** No random sideways jitter: a burst keeps its side. */
  still?: boolean;
}

/**
 * What one second of the room's totals floats: at most `perSec` new
 * reactions, the biggest kind first, a burst counting as one with its two
 * small companions. A burst, never a wall.
 */
export function floatsFor(counts: ReactionCounts, perSec: number): Float[] {
  const out: Float[] = [];
  let budget = perSec;
  let slot = 0;
  let bursts = 0;
  for (const [kind, n] of byCount(counts)) {
    if (budget <= 0) break;
    if (n >= BURST_FROM) {
      // Two bursts in one second take turns: one left and first, the next right and a beat later.
      const delayMs = slot++ * 170 + bursts * 520;
      const side = bursts % 2 ? 16 : -10;
      out.push({ kind, look: "burst", count: n, delayMs, shift: side, still: true });
      out.push({ kind, look: "kin", delayMs: delayMs + 110, shift: side - 28 });
      out.push({ kind, look: "kin", delayMs: delayMs + 260, shift: side + 26 });
      bursts++;
      budget--;
      continue;
    }
    for (let j = 0; j < n && budget > 0; j++, budget--) {
      out.push({ kind, look: "one", delayMs: slot++ * 190, shift: 0 });
    }
  }
  return out;
}

// ── the studio's line about the room ────────────────────────────────────
export const MOOD_WINDOW_MS = 30_000;
export const MOOD_STEP_MS = 5000;
/** Fewer taps than this in the window reads as listening quietly. */
const QUIET_BELOW = 4;

export interface Mood {
  /** "Mostly finding it useful", or "Listening quietly". Never a number. */
  words: string;
  /** The top three faces, the most first; none when the room is quiet. */
  faces: string[];
}

export const QUIET_MOOD: Mood = { words: "Listening quietly", faces: [] };

export function moodOf(counts: ReactionCounts): Mood {
  const top = byCount(counts);
  if (countOf(counts) < QUIET_BELOW || top.length === 0) return QUIET_MOOD;
  return {
    words: `Mostly ${REACTION_OF[top[0][0]].mood}`,
    faces: top.slice(0, 3).map(([k]) => REACTION_OF[k].emoji),
  };
}

export const sameMood = (a: Mood, b: Mood): boolean => a.words === b.words && a.faces.join("") === b.faces.join("");

/** The last thirty seconds of the room, as one line that moves on at most every five seconds. */
export class RoomMood {
  private heard: Array<{ at: number; counts: ReactionCounts }> = [];
  private said: Mood = QUIET_MOOD;
  private saidAt = Number.NEGATIVE_INFINITY;

  hear(nowMs: number, counts: ReactionCounts): void {
    this.heard.push({ at: nowMs, counts });
  }

  // Why every five seconds: the line should drift with the room, not flicker while the host talks.
  line(nowMs: number): Mood {
    if (nowMs - this.saidAt < MOOD_STEP_MS) return this.said;
    this.heard = this.heard.filter((h) => nowMs - h.at < MOOD_WINDOW_MS);
    const next = moodOf(this.heard.reduce<ReactionCounts>((all, h) => addCounts(all, h.counts), {}));
    this.saidAt = nowMs;
    if (!sameMood(next, this.said)) this.said = next;
    return this.said;
  }
}
